// Component: StockVideoModal
// Purpose: Displays a signed video URL in a modal for preview, with error
// handling and retry. Fetches a temporary signed URL from the server.
import React, { useState, useEffect } from 'react';
import { X, Play, AlertCircle } from 'lucide-react';
import { getStockVideoSignedUrl } from '../../../api/media/stockVideosApi';
import { LoadingSpinner } from '../../ui/feedback/loading-spinner.jsx';

export function StockVideoModal({ 
  isOpen, 
  onClose, 
  stockVideo
}) {
  const [signedUrl, setSignedUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && stockVideo?.id && !signedUrl) {
      fetchSignedUrl();
    }
  }, [isOpen, stockVideo?.id]);

  const fetchSignedUrl = async () => {
    if (!stockVideo?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('[StockVideoModal] Fetching signed URL for video ID:', stockVideo.id);
      const response = await getStockVideoSignedUrl(stockVideo.id);
      console.log('[StockVideoModal] Signed URL response:', response);
      
      if (response?.signedUrl) {
        setSignedUrl(response.signedUrl);
      } else {
        throw new Error('No signed URL received from server');
      }
    } catch (err) {
      console.error('[StockVideoModal] Error fetching signed URL:', err);
      
      // More detailed error handling
      if (err.response?.status === 404) {
        setError('Video not found or endpoint not available. Please check the server.');
      } else if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to load video. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSignedUrl(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80">
      <div className="relative w-full max-w-5xl bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b dark:border-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {stockVideo?.title || 'Stock Video'}
          </h3>
          <button 
            onClick={handleClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="h-6 w-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        
        <div className="aspect-video w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
          {isLoading ? (
            <div className="flex flex-col items-center space-y-3">
              <LoadingSpinner size="lg" />
              <p className="text-gray-600 dark:text-gray-400">Loading video...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center space-y-3 p-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500" />
              <div>
                <p className="text-red-600 dark:text-red-400 font-medium">{error}</p>
                <button
                  onClick={fetchSignedUrl}
                  className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : signedUrl ? (
            <video
              key={signedUrl} // Force re-render when URL changes
              className="w-full h-full"
              controls
              autoPlay
              preload="metadata"
            >
              <source src={signedUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="flex flex-col items-center space-y-3">
              <Play className="h-12 w-12 text-gray-400" />
              <p className="text-gray-600 dark:text-gray-400">Ready to play video</p>
            </div>
          )}
        </div>
        
        {stockVideo && (
          <div className="p-4 border-t dark:border-gray-800">
            <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span>Created: {new Date(stockVideo.createdAt).toLocaleDateString()}</span>
              <span>•</span>
              <span>Views: {stockVideo.totalClicks || 0}</span>
              {stockVideo.tags && stockVideo.tags.length > 0 && (
                <>
                  <span>•</span>
                  <span>Tags: {stockVideo.tags.slice(0, 3).join(', ')}{stockVideo.tags.length > 3 ? ` (+${stockVideo.tags.length - 3} more)` : ''}</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 